<script setup lang="ts">
import { computed, toRef } from "vue";
import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
import { resolveLocalizedTitle } from "@/composables/useCardData";
import { removeHtmlMarkdownAndLineBreak } from "@utils/toolBox";
import { Select } from "@/components/episto-ui/select";
import PopulationField from "./CardOptionFields/PopulationField.vue";
import type { CardQuestion, ChartCard } from "@/types";
import { cardCodes, closedQuestion, useAnalysisContext } from "./analysisData";
import OptionRow from "./CardOptionFields/OptionRow.vue";
import OptionSection from "./CardOptionFields/OptionSection.vue";

// card_token is the picked card; sides saved before it existed carry only question_id.
type Side = "source" | "target";

const props = defineProps({
  card: { type: Object, required: true },
});

const card = toRef(props, "card");
const cardsStore = useResultsV2CardsStore();
const { availableCards, displayLocale } = useAnalysisContext();

// A flow needs a discrete code per respondent on both sides: closed answers or a bounded scale.
const FLOW_TYPES = ["q_single", "q_multiple", "q_image_choice", "q_image_choice_multiple", "q_hidden", "q_nps", "q_rating", "q_matrix"];
const MATRIX_TYPES = ["single", "multiple", "rating", "nps"];

const flowCards = computed<ChartCard[]>(() =>
  (availableCards.value as ChartCard[]).filter((c) => {
    if (!FLOW_TYPES.includes(c.type || "") || !c.questions?.length) return false;
    return c.type !== "q_matrix" || !c.specs?.matrix_type || MATRIX_TYPES.includes(c.specs.matrix_type);
  }),
);

const questionOptions = computed(() => flowCards.value.map((c) => ({ value: c.token, label: c.name || c.token })));

const resolveTitle = (title: unknown) => removeHtmlMarkdownAndLineBreak(resolveLocalizedTitle(title, displayLocale.value));

function setContent(patch: Record<string, unknown>) {
  cardsStore.updateCardLocal(props.card.id, { content: { ...props.card.content, ...patch } });
}

// A loop repeats each item per iteration, so every item question stays listed.
function itemQuestions(meta: ChartCard | undefined): CardQuestion[] {
  const questions = (meta?.questions || []).filter((q) => q.item_token);
  return questions.length > 1 ? questions : [];
}

function useSide(side: Side) {
  const cardToken = computed<string | null>(() => props.card.content?.[`${side}_card_token`] || null);
  const questionId = computed<number | null>(() => props.card.content?.[`${side}_question_id`] || null);
  // An empty selection means every answer.
  const codes = computed<string[]>(() => props.card.content?.[`${side}_codes`] || []);

  const meta = computed<ChartCard | undefined>(() => {
    const cards = availableCards.value as ChartCard[];
    if (cardToken.value) return cards.find((c) => c.token === cardToken.value);
    return cards.find((c) => (c.questions || []).some((q) => q.id === questionId.value));
  });

  const itemOptions = computed(() => {
    const questions = itemQuestions(meta.value);
    const titles = questions.map((q, idx) => resolveTitle(q.item_title) || `Item ${idx + 1}`);
    return questions.map((q, idx) => {
      const duplicated = titles.some((title, i) => i !== idx && title === titles[idx]);
      return { value: q.id, label: duplicated && q.name ? `${titles[idx]} (${q.name})` : titles[idx] };
    });
  });

  const answerOptions = computed(() =>
    questionId.value ? cardCodes(meta.value, resolveTitle).map(({ code, label }) => ({ value: code, label })) : [],
  );

  // Changing the card or the item invalidates the answer selection.
  function pickCard(token: string) {
    const picked = flowCards.value.find((c) => c.token === token);
    const items = itemQuestions(picked);
    setContent({
      [`${side}_card_token`]: token,
      [`${side}_question_id`]: items.length ? null : (closedQuestion(picked)?.id ?? null),
      [`${side}_codes`]: [],
    });
  }

  const pickItem = (id: number | null) => setContent({ [`${side}_question_id`]: id, [`${side}_codes`]: [] });
  const pickCodes = (value: string[] | null) => setContent({ [`${side}_codes`]: value || [] });

  return { cardToken: computed(() => meta.value?.token ?? null), questionId, codes, itemOptions, answerOptions, pickCard, pickItem, pickCodes };
}

const source = useSide("source");
const target = useSide("target");
</script>

<template>
  <OptionSection
    id="data"
    title="Data">
    <OptionRow
      v-for="(side, key) in { source, target }"
      :key="key"
      :label="key === 'source' ? 'From (left side)' : 'To (right side)'"
      :hint="
        key === 'source'
          ? 'The nine biggest answers of each side keep their own node, the rest folds into Other. Pick answers below to focus the diagram; none selected shows every answer.'
          : ''
      ">
      <div class="space-y-2">
        <Select
          :model-value="side.cardToken.value"
          :options="questionOptions"
          :placeholder="key === 'source' ? 'Select the source question...' : 'Select the target question...'"
          filterable
          @update:model-value="side.pickCard($event)" />
        <Select
          v-if="side.itemOptions.value.length"
          :model-value="side.questionId.value"
          :options="side.itemOptions.value"
          placeholder="Item..."
          filterable
          @update:model-value="side.pickItem($event)" />
        <Select
          v-if="side.answerOptions.value.length"
          :model-value="side.codes.value"
          :options="side.answerOptions.value"
          placeholder="All answers"
          multiple
          filterable
          @update:model-value="side.pickCodes($event)" />
      </div>
    </OptionRow>

    <PopulationField :card="card" />
  </OptionSection>
</template>
